import React from "react";

import { ColumnType } from "antd/es/table";

import { formatNumber, trimToDecimals, useTranslation } from "utils";

import { FOOTPRINTING_CATEGORY_COLORS } from "consts";

import {
  DairyFootprintCalculationSummary,
  DairyResultsSubDataRowSource,
  FootprintingDairyEmissionsSourceCategory,
  FootprintingDairyResultsDataTableRow,
} from "models";

import { Dot, Flex, Icon, Table, TooltipWrapper } from "components";

interface FootprintingDairyResultTableProps {
  footprintCalculationSummary?: DairyFootprintCalculationSummary;
}

interface TableDataRow {
  key: React.Key;
  scope: string;
  source: React.Key;
  dot: React.ReactNode | null;
  totalCO2e: string;
  perKgYieldCO2e: string;
  perCowCO2e: string;
  shareOfTotal: string;
}

interface ColumnConfiguration extends ColumnType {
  title: React.ReactNode | string | null;
  dataIndex: keyof TableDataRow;
}

type CalculationSummaryTuple = [FootprintingDairyEmissionsSourceCategory, FootprintingDairyResultsDataTableRow];

// Category and subcategory scope mappings (Scope 1-3)
const DAIRY_SCOPE_MAPPING: Record<FootprintingDairyEmissionsSourceCategory, Partial<Record<DairyResultsSubDataRowSource, number>> | number> = {
  stockRenewal: 1,
  calfRearing: 1,
  feeding: 1,
  manure: 1,
  water: 1,
  energy: 1,
  machinery: 1,
  luc: 1,
  refrigerants: 1,
  waste: 1,
  grasslandFertiliser: 1,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const FootprintingDairyResultTable: React.FC<FootprintingDairyResultTableProps> = ({ footprintCalculationSummary }) => {
  const { t } = useTranslation(["footprinting"]);

  const columns: ColumnConfiguration[] = [
    { title: null, dataIndex: "dot" },
    { title: t("resultsPage.table.columnHeader.source"), dataIndex: "source" },
    {
      title: (
        <Flex gap={4}>
          {t("resultsPage.table.columnHeader.scope")}
          <TooltipWrapper className="scope-info-icon-container" tooltip={{ title: t("resultsPage.table.columnHeader.scopeTooltip"), placement: "top" }}>
            <Icon className="scope-info-icon" iconName="tabler/info-circle" size="s" />
          </TooltipWrapper>
        </Flex>
      ),
      dataIndex: "scope",
    },
    { title: t("resultsPage.table.columnHeader.totalCO2e"), dataIndex: "totalCO2e" },
    { title: t("resultsPage.table.columnHeader.perKgYieldCO2e"), dataIndex: "perKgYieldCO2e" },
    { title: t("resultsPage.table.columnHeader.perCowCO2e"), dataIndex: "perCowCO2e" },
    { title: t("resultsPage.table.columnHeader.shareOfTotal"), dataIndex: "shareOfTotal" },
  ];

  if (!footprintCalculationSummary) {
    return null;
  }

  const tableData = (Object.entries(footprintCalculationSummary?.tableData) as CalculationSummaryTuple[])
    .sort((a, b) => b[1].data.totalCO2e - a[1].data.totalCO2e)
    .flatMap(([category, row]): TableDataRow[] => {
      const categoryScope = DAIRY_SCOPE_MAPPING[category as FootprintingDairyEmissionsSourceCategory];
      const hasSubCategories = typeof categoryScope === "object" && Object.keys(categoryScope).length;
      const allSameScope = hasSubCategories && new Set(row.subData.map((subData) => categoryScope[subData?.source])).size === 1;
      const scopeValue = hasSubCategories
        ? allSameScope
          ? t("resultsPage.table.rowValue.scope", {
              scope: categoryScope[row.subData?.[0]?.source],
            })
          : t("resultsPage.table.rowValue.mixed")
        : t("resultsPage.table.rowValue.scope", {
            scope: categoryScope,
          });

      const categoryRow: TableDataRow = {
        key: category,
        source: t(`categories.${category}`),
        scope: String(scopeValue),
        dot: <Dot color={FOOTPRINTING_CATEGORY_COLORS[category as FootprintingDairyEmissionsSourceCategory]} />,
        totalCO2e: formatNumber({ number: trimToDecimals(row.data.totalCO2e, 2) }),
        perKgYieldCO2e: formatNumber({ number: trimToDecimals(row.data.perKgYieldCO2e, 2) }),
        perCowCO2e: formatNumber({ number: trimToDecimals(row.data.perCowCO2e, 2) }),
        shareOfTotal: formatNumber({ number: trimToDecimals(100 * row.data.shareOfTotal, 2) }),
      };

      return [categoryRow];
    });

  return (
    <Table
      className="flex-1"
      data={tableData}
      columns={columns}
      expandable={{
        expandedRowRender: () => <div className="results-table-hide-expanded-row" />,
      }}
    />
  );
};
